// HistoryBaseDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography, TextField, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { paymentServices } from 'src/services/payment/Payment.service';
import { billingFormServices } from 'src/services/payment/billingForm/billingForm.service';
import { CardCommon } from 'src/components/uiElements';
import dayjs from 'dayjs';

const HistoryBaseDialog = ({ open, onClose, record }) => {
  const [searchParams, setSearchParams] = useState({
    dateFrom: dayjs(),
    dateTo: dayjs().subtract(1, 'month'),
    currencyTo: null,
    currencyFrom: null,
    base: null,
  });
  const notify = useNotify()
  const translate = useTranslate()
  const [bases, setBases] = useState([]);
  const [currenciesFrom, setCurrenciesFrom] = useState([]);
  const [currenciesTo, setCurrenciesTo] = useState([]);
  const [currencyBaseRelations, setCurrencyBaseRelations] = useState([]);
  const [isLoadingCurrencyBase, setIsLoadingCurrencyBase] = useState(false);
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);
  const [resultHistoricalList, setResultHistoricalList] = useState([]);
  const [error, setError] = useState({}); // Estado para almacenar errores

  useEffect(() => {
    if (!open) {
      setSearchParams({
        dateFrom: dayjs().subtract(1, 'month'),
        dateTo: dayjs(),
        currencyTo: null,
        currencyFrom: null,
        base: null,
      });
      setCurrenciesFrom([])
      setCurrenciesTo([])
      setResultHistoricalList([])
      setError({});
    }
  }, [open]);

  useEffect(() => {
    if (record?.hash_work_unit) {
      getCurrencyBase(record.hash_work_unit);
    }
  }, [record]);

  const getCurrencyBase = async (workUnitHash) => {
    setIsLoadingCurrencyBase(true);
    try {
      const response = await billingFormServices.getCurrencyBaseRelations(`work_unit_hash=${workUnitHash}`);

      if (response?.data) {
        setCurrencyBaseRelations(response.data); // Guarda la respuesta para reutilizarla

        // Procesar las bases únicas
        const uniqueBases = Array.from(
          new Set(response.data.flatMap((item) => item.bases.map((base) => base.name)))
        );
        setBases(uniqueBases);
      }
    } catch (error) {
      console.error("Error fetching currency base relations:", error);
      setBases([]);
      setCurrenciesFrom([]);
      setCurrenciesTo([]);
    } finally {
      setIsLoadingCurrencyBase(false);
    }
  };

  const handleBaseChange = (newValue) => {
    // Restablece las monedas seleccionadas a null cuando cambia la base
    setSearchParams((prev) => ({
      ...prev,
      base: newValue,
      currencyFrom: null,  // Resetea moneda desde
      currencyTo: null,    // Resetea moneda hasta
    }));

    if (!currencyBaseRelations.length) return;

    const filteredItems = currencyBaseRelations.filter((item) =>
      item.bases.some((base) => base.name === newValue)
    );

    const uniqueCurrenciesFrom = Array.from(
      new Set(filteredItems.map((item) => item.currency_from_relation.short_name))
    );

    const uniqueCurrenciesTo = Array.from(
      new Set(filteredItems.map((item) => item.currency_to_relation.short_name))
    );

    setCurrenciesFrom(uniqueCurrenciesFrom);
    setCurrenciesTo(uniqueCurrenciesTo);

    if (newValue) {
      setError((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.base; // Elimina el error para la base
        return newErrors;
      });
    }
  };

  const handleCurrencyFromChange = (newValue) => {
    setSearchParams((prev) => ({ ...prev, currencyFrom: newValue }));
    if (newValue) {
      setError((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.currencyFrom; // Elimina el error para currencyFrom
        return newErrors;
      });
    }
  };

  const handleCurrencyToChange = (newValue) => {
    setSearchParams((prev) => ({ ...prev, currencyTo: newValue }));
    if (newValue) {
      setError((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.currencyTo; // Elimina el error para currencyTo
        return newErrors;
      });
    }
  };

  const handleDateChange = (key, value) => {
    setSearchParams((prev) => ({ ...prev, [key]: value }));
  };

  const validateFields = () => {
    const newError = {};

    // Validar los campos y guardar errores si faltan
    if (!searchParams.dateFrom) newError.dateFrom = translate('ra.validation.fieldIsRequired');
    if (!searchParams.dateTo) newError.dateTo = translate('ra.validation.fieldIsRequired');
    if (!searchParams.base) newError.base = translate('ra.validation.fieldIsRequired');
    if (!searchParams.currencyFrom) newError.currencyFrom = translate('ra.validation.fieldIsRequired');
    if (!searchParams.currencyTo) newError.currencyTo = translate('ra.validation.fieldIsRequired');

    setError(newError);

    // Si hay errores, no permitir el filtro
    return Object.keys(newError).length === 0;
  };

  const handleFilter = async () => {
    if (!validateFields()) return; // Si no pasa la validación, no ejecuta el filtro
    try {
      setIsLoadingFilter(true)
      const historicalList = await billingFormServices.getHistoricalCurrencyHistoryBases({
        bases: searchParams.base || null,
        created_at_after: searchParams.dateFrom ? dayjs(searchParams.dateFrom).format("YYYY-MM-DD") : null,
        created_at_before: searchParams.dateTo ? dayjs(searchParams.dateTo).add(1, 'day').format("YYYY-MM-DD") : null,
        currency_from: searchParams.currencyFrom || null,
        currency_to: searchParams.currencyTo || null,
        work_unit_hash: record.hash_work_unit || null,
      })

      setResultHistoricalList(historicalList?.data || []);
    } catch (error) {
      console.error("Error al filtrar reservas:", error);

    } finally {
      setIsLoadingFilter(false)
    }
  };

  const handleDownload = async () => {
    if (!validateFields()) return; // Si no pasa la validación, no ejecuta el filtro
    try {
      setIsLoadingDownload(true)
      const historicalDownload = await billingFormServices.getHistoricalCurrencyHistoryBases({
        bases: searchParams.base || null,
        created_at_after: searchParams.dateFrom ? dayjs(searchParams.dateFrom).format("YYYY-MM-DD") : null,
        created_at_before: searchParams.dateTo ? dayjs(searchParams.dateTo).add(1, 'day').format("YYYY-MM-DD") : null,
        currency_from: searchParams.currencyFrom || null,
        currency_to: searchParams.currencyTo || null,
        work_unit_hash: record.hash_work_unit || null,
        download_excel: true
      })

      const fileURL = URL.createObjectURL(historicalDownload);
      const a = document.createElement('a');
      a.href = fileURL; 
      a.download = `historical_currency_data_${searchParams.base}.xlsx`; 
      document.body.appendChild(a);
      a.click(); 
      a.remove(); 
      URL.revokeObjectURL(fileURL);
      notify('Descarga realizada con éxito.', { type: 'success' });
    } catch (error) {
      notify('No se pudo realizar la descarga.', { type: 'error' });
    } finally {
      setIsLoadingDownload(false)
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="dialog-product"
    >
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CardCommon title={translate('ra.title.exchangeRateHistory')} style={{ marginBottom: 12 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      label={translate('ra.field.from')}
                      value={searchParams.dateFrom || dayjs().subtract(1, 'month')}
                      onChange={(newValue) => handleDateChange('dateFrom', newValue)}
                      maxDate={dayjs()} // No permite seleccionar fechas futuras
                      renderInput={(params) => <TextField fullWidth {...params} />}
                      inputFormat="DD/MM/YYYY" // Establece el formato aquí
                      error={!!error.dateFrom} // Mostrar error si el campo está vacío
                      helperText={error.dateFrom} // Mostrar mensaje de error
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {/* Fecha Hasta */}
                  <Grid item xs={6}>
                    <DatePicker
                      label={translate('ra.field.to')}
                      value={searchParams.dateTo || dayjs()} // Por defecto es hoy
                      onChange={(newValue) => handleDateChange('dateTo', newValue)}
                      minDate={searchParams.dateFrom}
                      maxDate={dayjs()}
                      renderInput={(params) => <TextField fullWidth {...params} />}
                      inputFormat="DD/MM/YYYY" // Establece el formato de la fecha
                      error={!!error.dateTo}
                      helperText={error.dateTo}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
              <Grid container spacing={2} style={{ marginTop: 16 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={bases}
                    value={searchParams.base}
                    onChange={(event, newValue) => handleBaseChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Base"
                        fullWidth
                        error={!!error.base}
                        helperText={error.base}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} style={{ marginTop: 16 }}>
                <Grid item xs={6}>
                  <Autocomplete
                    options={currenciesFrom}
                    value={searchParams.currencyFrom}
                    onChange={(event, newValue) => handleCurrencyFromChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={translate('ra.field.currency')}
                        fullWidth
                        error={!!error.currencyFrom}
                        helperText={error.currencyFrom}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={currenciesTo}
                    value={searchParams.currencyTo}
                    onChange={(event, newValue) => handleCurrencyToChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={translate('ra.field.currency')}
                        fullWidth
                        error={!!error.currencyTo}
                        helperText={error.currencyTo}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {/* Botón Filtrar */}
              <Grid container justifyContent="center" style={{ marginTop: 16 }}>
                <Grid item>
                  <Button
                    onClick={() => handleFilter()}
                    color="primary"
                    variant="contained"
                    fullWidth
                    startIcon={isLoadingFilter ? <CircularProgress size={24} color="inherit" /> : null}
                  >
                    {translate('ra.action.filter')}
                  </Button>
                </Grid>
              </Grid>
            </CardCommon>
          </Grid>
          {isLoadingFilter ? (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ height: '100px' }} // Ajusta la altura según tu diseño
            >
              <CircularProgress size={24} color="inherit" />
            </Grid>
          ) : resultHistoricalList.length > 0 ? (
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5', boxShadow: 1, borderRadius: 1 }}>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.pickup_date')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.currency')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.currency')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.value')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.is_active')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.isInherited')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4b4b4b' }}>{translate('ra.field.username')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultHistoricalList.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{dayjs(row.created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                        <TableCell>{row.currency_from_relation.short_name}</TableCell>
                        <TableCell>{row.currency_to_relation.short_name}</TableCell>
                        <TableCell>{row.value}</TableCell>
                        <TableCell>{row.is_active === true ? 'Si' : 'No'}</TableCell>
                        <TableCell>{row.is_inherited === true ? 'Si' : 'No'}</TableCell>
                        <TableCell>{row.username ? row.username : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ) : (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ height: '100px' }} // Ajusta la altura según tu diseño
            >
              <Typography variant="body1" color="textSecondary">
                {translate('ra.message.noResultsAvailable')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={() => handleDownload()}
          color="primary"
          variant="contained"
          disabled={resultHistoricalList.length === 0}
          startIcon={isLoadingDownload ? <CircularProgress size={24} color="inherit" /> : null}
        >
          {translate('ra.action.download')}
        </Button>
      </DialogActions>
    </Dialog>

  );
};

export default HistoryBaseDialog;
